export type Op = { op: string; [k: string]: unknown };

// Mirrors the Wippy `app.excel:ops_schema` vocabulary, which in turn mirrors the
// server xlsx ops-log (app.office.knowledge.xlsx:authoring/:editing). Keep the
// three in sync — both sides of the bridge must agree on op names and fields.
const KNOWN_OPS = new Set([
  "sheet", "style", "col", "row", "freeze", "merge", "unmerge", "clear",
  "cell", "rows", "insert_rows", "insert_cols", "delete_rows", "delete_cols",
  "rename_sheet", "delete_sheet", "autofit_cols", "autofit_rows", "comment",
  "conditional_format", "chart", "data_validation", "copy_sheet", "copy_range",
  "create_table", "hyperlink", "format", "sort", "autofilter", "define_name",
  "pivot",
]);

// `sheet` is intentionally NOT required on content ops: an op may omit it to
// target the most-recently-selected sheet (see the current-sheet tracking in
// the apply_ops handler), exactly as the server ops-log does.
const REQUIRED: Record<string, string[]> = {
  sheet: ["name"],
  cell: ["ref"],
  rows: ["at", "rows"],
  style: ["id"],
  merge: ["range"],
  unmerge: ["range"],
  clear: ["range"],
  freeze: ["ref"],
  col: ["col", "width"],
  row: ["row", "height"],
  insert_rows: ["at", "count"],
  insert_cols: ["at", "count"],
  delete_rows: ["at", "count"],
  delete_cols: ["at", "count"],
  rename_sheet: ["from", "to"],
  delete_sheet: ["name"],
  comment: ["ref", "text"],
  conditional_format: ["range", "rule"],
  chart: ["data_range", "chart_type"],
  data_validation: ["range", "rule"],
  copy_sheet: ["from", "to"],
  copy_range: ["from", "to"],
  create_table: ["range"],
  hyperlink: ["ref"],
  format: ["range"],
  sort: ["range", "by"],
  autofilter: ["range"],
  define_name: ["name", "range"],
  pivot: ["source", "dest", "rows", "values"],
};

// A `freeze` op's `ref` is the first UNFROZEN cell (the Excel UI convention:
// select the cell below/right of what you want frozen, then Freeze Panes). This
// translates it into how many top rows and left columns to freeze. "A4" -> the
// header rows 1-3 freeze, no columns: { rows: 3, cols: 0 }.
//
// This exists because Office.js `freezePanes.freezeAt(range)` interprets its
// argument as the frozen REGION, not the first-unfrozen cell — so handing it a
// mid-sheet single cell like "A4" strands rows 1-3 above the split where the
// user cannot scroll to them. The interpreter uses these counts with
// freezeRows/freezeColumns, which have unambiguous semantics, instead.
//
// Returns null for anything that is not a plain A1-style single cell (a range,
// garbage, a non-string) so the caller can fall back to the raw freezeAt.
export function freezeSpec(ref: unknown): { rows: number; cols: number } | null {
  if (typeof ref !== "string") return null;
  const m = ref.replace(/\$/g, "").trim().match(/^([A-Za-z]{1,3})(\d+)$/);
  if (!m) return null;
  const letters = m[1].toUpperCase();
  let col = 0;
  for (let i = 0; i < letters.length; i++) col = col * 26 + (letters.charCodeAt(i) - 64);
  const row = parseInt(m[2], 10);
  return { rows: Math.max(0, row - 1), cols: Math.max(0, col - 1) };
}

export type NormalizeResult =
  | { ok: true; ops: Op[] }
  | { ok: false; index: number; message: string };

export function normalizeOps(raw: unknown): NormalizeResult {
  if (!Array.isArray(raw)) return { ok: false, index: -1, message: "ops must be an array" };
  for (let i = 0; i < raw.length; i++) {
    const op = raw[i] as Op;
    if (!op || typeof op !== "object" || typeof op.op !== "string")
      return { ok: false, index: i, message: "missing op field" };
    if (!KNOWN_OPS.has(op.op))
      return { ok: false, index: i, message: `unknown op: ${op.op}` };
    for (const f of REQUIRED[op.op] ?? [])
      if (op[f] === undefined) return { ok: false, index: i, message: `${op.op} missing field: ${f}` };
    // A `cell` op may carry a value/formula (content) OR only styling (a style-only
    // op re-formats an existing cell without touching its content). Reject only a
    // truly-empty cell op (op/sheet/ref and nothing else).
    if (op.op === "cell" && op.value === undefined && op.formula === undefined) {
      const hasStyling = Object.keys(op).some((k) => k !== "op" && k !== "sheet" && k !== "ref");
      if (!hasStyling)
        return { ok: false, index: i, message: "cell needs a value, a formula, or a style" };
    }
  }
  return { ok: true, ops: raw as Op[] };
}
